/**
 * Shared helpers for the public tracking timeline — a CSS-Modules /
 * react-icons port of the internal TMS `journey-utils.ts`.
 */
import type { IconType } from 'react-icons';
import {
  FaPlane,
  FaRegClock,
  FaLayerGroup,
  FaTruck,
  FaPaperPlane,
  FaBoxOpen,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaLocationArrow,
  FaExclamationTriangle,
  FaShieldAlt,
  FaInfoCircle,
} from 'react-icons/fa';
import { FaBuilding } from 'react-icons/fa6';

// ─── Icon resolver ──────────────────────────────────────────────────
// The backend ships the pipeline-stage icon as a string ("Package",
// "Building2", "Plane", …). Map those Lucide names to the closest
// react-icons component.
const ICONS_BY_NAME: Record<string, IconType> = {
  Activity: FaRegClock,
  Building2: FaBuilding,
  CheckCircle2: FaCheckCircle,
  Layers: FaLayerGroup,
  MapPin: FaMapMarkerAlt,
  Package: FaBoxOpen,
  Plane: FaPlane,
  Send: FaPaperPlane,
  Truck: FaTruck,
  Navigation: FaLocationArrow,
  AlertTriangle: FaExclamationTriangle,
  ShieldAlert: FaShieldAlt,
  Clock: FaRegClock,
  Info: FaInfoCircle,
};

export function iconForName(name?: string | null): IconType {
  if (!name) return FaRegClock;
  return ICONS_BY_NAME[name] ?? FaRegClock;
}

// eventType → icon name (resolved through iconForName). Mirrors the node
// styling on the internal timeline so each public step shows a matching icon;
// unknown/dynamic types fall back to the common clock.
const EVENT_ICON_NAMES: Record<string, string> = {
  BOOKING_CREATED: 'Package',
  BOOKING_CONFIRMED: 'CheckCircle2',
  BOOKING_ACCEPTED: 'CheckCircle2',
  BOOKING_BAGGED: 'Layers',
  PICKUP_SCANNED: 'Package',
  PICKED_UP: 'Package',
  BRANCH_IN: 'Building2',
  BRANCH_OUT: 'Building2',
  PACKED_STAGE1: 'Package',
  PACKED_STAGE2: 'Package',
  INTO_BAG: 'Layers',
  BAG_BOX_ADDED: 'Layers',
  BAG_SEALED: 'Layers',
  BAG_RECEIVED: 'Building2',
  BAG_IN_TRANSIT: 'Truck',
  BAG_LOADED_ON_TRIP: 'Truck',
  LOADED_ON_TRIP: 'Truck',
  INTO_MAWB: 'Plane',
  BAG_INTO_MAWB: 'Plane',
  MAWB_CREATED: 'Plane',
  MAWB_PENDING_APPROVAL: 'Clock',
  MAWB_SEALED: 'Layers',
  MAWB_XRAY_CLEARED: 'CheckCircle2',
  MAWB_ASSIGNED_FOR_DELIVERY: 'Truck',
  MAWB_DISPATCHED: 'Navigation',
  MAWB_HANDED_TO_CARRIER: 'Plane',
  MAWB_DEPARTED: 'Plane',
  MAWB_ARRIVED_DESTINATION: 'MapPin',
  MAWB_RECEIVED_AT_BRANCH: 'Building2',
  MAWB_DELIVERED_TO_AGENT: 'CheckCircle2',
  MAWB_VENDOR_STATUS_UPDATE: 'Plane',
  TRIP_DISPATCHED: 'Truck',
  TRIP_ARRIVED: 'MapPin',
  TRIP_DELIVERED: 'CheckCircle2',
  OUT_FOR_DELIVERY: 'Send',
  HANDED_TO_COURIER: 'Truck',
  HANDED_TO_VENDOR: 'Truck',
  DELIVERED: 'CheckCircle2',
  RTO_DELIVERED: 'Package',
  HELD_BY_CUSTOMS: 'ShieldAlert',
  EXCEPTION: 'AlertTriangle',
  DAMAGED: 'ShieldAlert',
  OPS_STATUS_UPDATE: 'Info',
};

export function iconNameForEventType(eventType?: string | null): string {
  if (!eventType) return 'Clock';
  return EVENT_ICON_NAMES[eventType] ?? 'Clock';
}

// eventType → friendly one-line sub-title shown under each public step. Plain
// customer language; unknown/dynamic types return null (row falls back to the
// event note, else no sub-line).
const EVENT_DESCRIPTIONS: Record<string, string> = {
  BOOKING_CREATED: 'Your shipment is booked',
  BOOKING_CONFIRMED: 'Your booking is confirmed',
  PICKUP_SCANNED: 'Pickup scheduled',
  PICKED_UP: 'Collected from the sender',
  BRANCH_IN: 'Received at our branch',
  BRANCH_OUT: 'Left the branch',
  PACKED_STAGE1: 'Being packed',
  PACKED_STAGE2: 'Packed and ready',
  INTO_BAG: 'Packed for shipping',
  BAG_BOX_ADDED: 'Packed for shipping',
  BAG_SEALED: 'Packed and sealed',
  BAG_RECEIVED: 'Received at our facility',
  BAG_IN_TRANSIT: 'On its way',
  BAG_LOADED_ON_TRIP: 'Loaded for transfer',
  LOADED_ON_TRIP: 'Loaded for transfer',
  TRIP_DISPATCHED: 'On its way to the next facility',
  TRIP_ARRIVED: 'Arrived at the destination',
  TRIP_DELIVERED: 'Delivered to the recipient',
  INTO_MAWB: 'Prepared for export',
  BAG_INTO_MAWB: 'Prepared for export',
  MAWB_CREATED: 'Prepared for export',
  MAWB_PENDING_APPROVAL: 'Awaiting export clearance',
  MAWB_SEALED: 'Prepared for export',
  MAWB_XRAY_CLEARED: 'Cleared by security',
  MAWB_ASSIGNED_FOR_DELIVERY: 'Assigned to the airline',
  MAWB_DISPATCHED: 'On its way to the airport',
  MAWB_HANDED_TO_CARRIER: 'Handed to the airline',
  MAWB_DEPARTED: 'On its way to the destination',
  MAWB_ARRIVED_DESTINATION: 'Arrived at the destination',
  MAWB_RECEIVED_AT_BRANCH: 'Arrived at destination facility',
  MAWB_DELIVERED_TO_AGENT: 'Delivered',
  MAWB_VENDOR_STATUS_UPDATE: 'Carrier update',
  OPS_STATUS_UPDATE: 'Status update',
  OUT_FOR_DELIVERY: 'Out for delivery',
  HANDED_TO_COURIER: 'Out for delivery',
  HANDED_TO_VENDOR: 'Travelling with our partner carrier',
  DELIVERED: 'Delivered to the recipient',
  RTO_DELIVERED: 'Returned to sender',
  HELD_BY_CUSTOMS: 'Held at customs',
  EXCEPTION: 'An update on your shipment',
};

export function descForEventType(eventType?: string | null): string | null {
  if (!eventType) return null;
  return EVENT_DESCRIPTIONS[eventType] ?? null;
}

// ─── Date / time formatters ─────────────────────────────────────────

export function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Short "18 Nov" style date. */
export function shortDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function fmtRelativeTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
  return fmtDate(iso);
}

/** "18 Nov · 14:32" combined date + time. */
export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = shortDate(iso);
  const time = fmtTime(iso);
  return time ? `${date} · ${time}` : date;
}

// ─── Status / label prettifiers ─────────────────────────────────────

/** Turn an UPPER_SNAKE or free-text status into Title Case. */
export function prettyStatus(raw?: string | null): string {
  if (!raw) return '—';
  return String(raw)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Carrier / vendor status — UPPER_SNAKE → "Title Case", free text kept. */
export function prettyVendorStatus(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) return 'Update';
  const s = raw.trim();
  if (/^[A-Z0-9_]+$/.test(s)) {
    return s
      .toLowerCase()
      .split('_')
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ');
  }
  return s;
}

/** Turn an event type (UPPER_SNAKE) into a friendly label. */
export function prettyEventLabel(raw?: string | null): string {
  if (!raw) return 'Update';
  return prettyStatus(raw);
}
